import { FC, ReactNode, useCallback, useState } from 'react'

import { AppDialog } from '@/components/AppDialog'
import { Divider } from '@/components/Divider'
import AttentionIcon from '@/components/icons/attention.svg?react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface Props {
  isOpen: boolean
  onClose: (reason: 'closeModal' | 'cancel') => void
  onConfirm?: () => void
  confirmLoading?: boolean
  content: ReactNode | string
  helperText?: ReactNode
  confirmText?: string
  cancelText?: string
  Icon?: FC<React.SVGProps<SVGSVGElement>>
  title: string
}

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  confirmText,
  cancelText,
  Icon,
  title,
  content,
  confirmLoading,
  helperText,
}: Props) => {
  return (
    <AppDialog
      hideCloseButton
      isOpen={isOpen}
      onOpenChange={() => onClose('closeModal')}
      className='!max-w-md'
      dialogContentClassName='p-0'
      drawerContentClassName='!p-0'
    >
      <div className='mb-3 flex flex-col items-center gap-4'>
        {Icon ? <Icon className='size-8' /> : <AttentionIcon className='size-8' />}
        <div className='text-lg font-medium'>{title}</div>
      </div>
      <p className='text-text-secondary p-6 pt-0 text-center text-base'>{content}</p>
      {helperText && <p className='text-text-secondary p-6 pt-0 text-center text-base'>{helperText}</p>}
      <Divider />
      <div className='flex items-center justify-end gap-3 p-6'>
        {confirmText && (
          <Button
            loading={confirmLoading}
            variant='ghost'
            disabled={confirmLoading}
            size='sm'
            onClick={onConfirm}
            className={cn('text-button-secondary-border-default text-base', confirmLoading !== undefined && 'relative min-w-fit')}
          >
            {confirmText}
          </Button>
        )}
        {cancelText && (
          <Button size='sm' variant='ghost' onClick={() => onClose('cancel')} className='text-text-secondary text-base'>
            {cancelText}
          </Button>
        )}
      </div>
    </AppDialog>
  )
}

interface UseConfirmOptions {
  title?: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
  helperText?: ReactNode
}

export const useConfirm = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null)
  const [dialogProps, setDialogProps] = useState<UseConfirmOptions | null>(null)

  const confirm = useCallback((options: UseConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      setDialogProps(options)
      setResolvePromise(() => resolve)
      setIsOpen(true)
    })
  }, [])

  const handleConfirm = () => {
    if (resolvePromise) {
      resolvePromise(true)
      setResolvePromise(null)
      setIsOpen(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open && resolvePromise) {
      resolvePromise(false)
      setResolvePromise(null)
    }
  }

  const ConfirmDialogComponent = dialogProps ? (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={() => handleOpenChange(false)}
      title={dialogProps.title || 'Confirm'}
      content={dialogProps.description}
      confirmText={dialogProps.confirmLabel || 'Confirm'}
      cancelText={dialogProps.cancelLabel || 'Cancel'}
      onConfirm={handleConfirm}
      helperText={dialogProps.helperText}
    />
  ) : null

  return {
    confirm,
    ConfirmDialog: ConfirmDialogComponent,
  }
}
